import React, { useState } from 'react';

interface LinkUsedPageProps {
  hospitalId?: string;
  logoUrl?: string | null;
}

/**
 * Get hospital logo path based on hospital_id and logo_url
 * 
 * Priority:
 * 1. Use logo_url from API response if available
 * 2. Try known hospital mappings (SCUB, SJBM)
 * 3. Fallback to default AXA Medis logo
 */
const getHospitalLogo = (hospitalId: string | undefined, logoUrl?: string | null): string => {
  // Priority 1: Use logo_url from API response
  if (logoUrl) {
    return logoUrl;
  }
  
  if (!hospitalId) {
    return '/assets/logo/logo.png';
  }
  
  // Priority 2: Map of known hospital IDs to their specific logo filenames
  const hospitalLogos: Record<string, string> = {
    // SCUB - Spitalul Clinic de Urgență București
    '827bd4c6-79f6-410f-bbf5-20759ea44bd7': '/assets/logo/scub.png',
    // SJBM - Spitalul Județean de Urgență Baia Mare
    'c3043559-9ddc-4e08-ae7f-1fb58d6aa6b3': '/assets/logo/sjbm_logo.png',
  };
  
  // Priority 3: Fallback to default
  return hospitalLogos[hospitalId] || '/assets/logo/logo.png';
};

const LinkUsedPage: React.FC<LinkUsedPageProps> = ({ hospitalId, logoUrl }) => {
  const [logoError, setLogoError] = useState(false);
  const logoSrc = getHospitalLogo(hospitalId, logoUrl);

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex items-center justify-center p-4 sm:p-6">
      <div className="relative px-4 pb-12 pt-24 w-full max-w-lg">
        
        {/* --- 1. HEADER --- */}
        {/* Blue gradient header with rounded corners and fade effect */}
        <div className="absolute top-0 left-0 w-full h-80 bg-blue-600 overflow-hidden z-0 rounded-[2rem] [mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)]">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700" />
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-400 rounded-full mix-blend-overlay filter blur-3xl opacity-20" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-40" />
        </div>

        {/* --- 2. MAIN CARD --- */}
        <div className="mx-auto bg-white rounded-[2rem] shadow-2xl shadow-slate-900/10 border border-slate-100 relative z-10">
          
          {/* --- FLOATING BADGE (Hospital Logo) --- */}
          <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 z-20">
            <div className="w-40 h-40 sm:w-44 sm:h-44 bg-white rounded-full shadow-xl p-3 flex items-center justify-center">
              <div className="w-full h-full bg-slate-50 rounded-full border border-slate-100 flex items-center justify-center p-5 overflow-hidden">
                {!logoError ? (
                  <img 
                    src={logoSrc}
                    alt="Hospital Logo" 
                    className="w-full h-full object-contain"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  // Fallback icon if logo fails to load
                  <svg className="w-16 h-16 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                )}
              </div>
            </div>
          </div>

          {/* --- CONTENT --- */}
          <div className="pt-24 pb-8 px-6 sm:px-8 text-center relative">
            
            {/* Status Icon (Lock/Secure) */}
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center ring-8 ring-amber-50/50">
                <svg className="w-10 h-10 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-3 tracking-tight">
              Link deja utilizat
            </h1>
            
            {/* Subheading */}
            <p className="text-slate-500 text-sm sm:text-base leading-relaxed mb-8 px-2">
              Acest chestionar de feedback a fost deja completat și înregistrat în sistemul nostru.
            </p>

            {/* Security/Info Box */}
            <div className="bg-amber-50/50 rounded-2xl p-5 border border-amber-100 mb-8 text-left">
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 p-2 rounded-lg shrink-0">
                  <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 text-sm mb-1">Măsură de securitate</h4>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Pentru a asigura veridicitatea datelor, fiecare link de feedback este unic și poate fi utilizat o singură dată.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer / Exit Instruction - NO BUTTONS HERE */}
            <div className="border-t border-slate-100 pt-6 mt-2">
              <p className="text-slate-400 text-xs flex items-center justify-center gap-1.5">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Puteți închide această pagină.
              </p>
            </div>

          </div>
        </div>
        
        {/* Copyright Footer */}
        <p className="text-center text-slate-400/80 text-xs mt-8 relative z-10">
          © 2025 AXA Medis.
        </p>
      </div>
    </div>
  );
};

export default LinkUsedPage;